import axios from 'axios';
import { setupAuthInterceptor } from '../common';
import { NAMESPACES } from '../configuration';
import { FEATURE_ROLLOUT_REQUEST_TIMEOUT_MS } from '../../configuration/directMenuRollout';

const api = setupAuthInterceptor(axios.create({
  baseURL: NAMESPACES.management_api.settings_delegation,
  timeout: FEATURE_ROLLOUT_REQUEST_TIMEOUT_MS,
}));

export interface SettingsAccess { read: boolean; edit: boolean }
export const referenceFields = ['moneda_id', 'language_id', 'timezone_id', 'main_caja_id'] as const;
export type ReferenceField = typeof referenceFields[number];
export type SettingsValues = Record<ReferenceField, number | null> & {
  print_orders: boolean | null;
  local_open: boolean | null;
  default_tip: string | null;
};
export interface SettingsDocument {
  values: SettingsValues;
  catalogs: Record<ReferenceField, Array<{ id: number; label: string }>>;
  can_edit: boolean;
}
export interface SettingsEditor { user_id: number; username: string; can_edit: boolean; revision: number; eligible: boolean }

function object(value: unknown): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) throw new Error('Invalid settings response');
  return value as Record<string, unknown>;
}
function positive(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value > 0;
}
function nullableBoolean(value: unknown): value is boolean | null {
  return value === null || typeof value === 'boolean';
}
function databaseBoolean(value: unknown): value is boolean | 0 | 1 {
  return typeof value === 'boolean' || value === 0 || value === 1;
}
function values(value: unknown): SettingsValues {
  const row = object(value);
  for (const key of referenceFields) {
    if (row[key] !== null && !positive(row[key])) throw new Error('Invalid settings reference');
  }
  if (!nullableBoolean(row.print_orders) || !nullableBoolean(row.local_open)
    || (row.default_tip !== null && (typeof row.default_tip !== 'string'
      || !/^(0|[1-9]\d{0,2})\.\d{2}$/.test(row.default_tip)
      || Number(row.default_tip) > 100))) throw new Error('Invalid settings values');
  return row as SettingsValues;
}

/** Uncached access probe rechecks server-side grants instead of trusting localStorage. */
export async function getSettingsAccess(localId: number, signal?: AbortSignal): Promise<SettingsAccess> {
  const data = object((await api.get(`/Capabilities/${localId}`, { signal })).data);
  if (typeof data.read !== 'boolean' || typeof data.edit !== 'boolean' || (data.edit && !data.read)) {
    throw new Error('Invalid settings capabilities');
  }
  return { read: data.read, edit: data.edit };
}

/** Load the current general-settings snapshot and only same-local reference choices. */
export async function getDelegatedSettings(localId: number, signal?: AbortSignal): Promise<SettingsDocument> {
  const data = object((await api.get(`/Configuration/${localId}`, { signal })).data);
  const rawCatalogs = object(data.catalogs);
  const catalogs: SettingsDocument['catalogs'] = { moneda_id: [], language_id: [], timezone_id: [], main_caja_id: [] };
  for (const key of referenceFields) {
    const rows = rawCatalogs[key];
    if (!Array.isArray(rows)) throw new Error('Invalid settings catalog');
    catalogs[key] = rows.map(value => {
      const row = object(value);
      if (!positive(row.id) || typeof row.label !== 'string') throw new Error('Invalid catalog entry');
      return { id: row.id, label: row.label };
    });
  }
  if (typeof data.can_edit !== 'boolean') throw new Error('Invalid edit grant');
  return { values: values(data.values), catalogs, can_edit: data.can_edit };
}

/** Explicit compare-and-set; neither ambiguous writes nor conflicts are retried. */
export async function saveDelegatedSettings(
  localId: number, expected: SettingsValues, next: SettingsValues, signal?: AbortSignal,
): Promise<SettingsValues> {
  return values(object((await api.put(`/Configuration/${localId}`, { expected, values: next }, { signal })).data).values);
}

/** Roster visible only to same-local superadmins; no application permissions are changed. */
export async function getSettingsEditors(localId: number, signal?: AbortSignal): Promise<SettingsEditor[]> {
  const data = object((await api.get(`/Editors/${localId}`, { signal })).data);
  if (!Array.isArray(data.editors)) throw new Error('Invalid editors');
  return data.editors.map(value => {
    const row = object(value);
    if (!positive(row.user_id) || typeof row.username !== 'string'
      || !databaseBoolean(row.can_edit) || !databaseBoolean(row.eligible)
      || typeof row.revision !== 'number' || !Number.isSafeInteger(row.revision) || row.revision < 0) {
      throw new Error('Invalid editor');
    }
    return { user_id: row.user_id, username: row.username, can_edit: Boolean(row.can_edit),
      revision: row.revision, eligible: Boolean(row.eligible) };
  });
}

/** Grant or revoke editing separately from permission 61 and flag activation. */
export async function assignSettingsEditor(
  localId: number, editor: SettingsEditor, canEdit: boolean, signal?: AbortSignal,
): Promise<void> {
  await api.put(`/Editors/${localId}/${editor.user_id}`, {
    can_edit: canEdit, expected_revision: editor.revision,
  }, { signal });
}
